import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

/**
 * Resource-person marketplace slice.
 *
 *   GET    /api/resource-person/marketplace/{type}
 *   POST   /api/resource-person/marketplace/{type}
 *   GET    /api/resource-person/marketplace/{type}/{id}
 *   PATCH  /api/resource-person/marketplace/{type}/{id}
 *   DELETE /api/resource-person/marketplace/{type}/{id}
 *   PATCH  /api/resource-person/marketplace/{type}/{id}/submit
 *   PATCH  /api/resource-person/marketplace/{type}/{id}/unsubmit
 *
 * `type` is one of project-topics | seminar-topics | research-materials | manuals.
 */

export const MARKETPLACE_TYPES = [
  "project-topics",
  "seminar-topics",
  "research-materials",
  "manuals",
];

export const resourcePersonMarketplaceInitialState = {
  marketplaceItems: [],
  marketplaceLoading: false,
  marketplaceError: null,
  marketplaceTotal: 0,
  marketplacePages: 1,
  marketplaceResourceType: "",
  marketplaceItem: null,
  marketplaceItemLoading: false,
  marketplaceItemError: null,
  marketplaceMutationLoading: false,
  marketplaceMutationError: null,
};

function assertType(type) {
  if (!MARKETPLACE_TYPES.includes(type)) {
    throw new Error("Unknown marketplace resource type.");
  }
  return type;
}

function buildUrl(path, params = {}) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== "")
  ).toString();
  return `${API_URL.replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`;
}

async function request(path, { method = "GET", params, body } = {}) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const token = authSession.getToken();
  const response = await safeFetch(buildUrl(path, params), {
    method,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "omit",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(`Marketplace API returned a non-JSON response (${response.status}).`);
    }
  }
  if (!response.ok || data.error || data.success === false) {
    throw new Error(
      explainApiError(response.status, data || {}, `Request failed with status ${response.status}.`)
    );
  }
  return data?.data && typeof data.data === "object" ? data.data : data;
}

function itemOf(data) {
  if (data?.item && typeof data.item === "object") return data.item;
  if (data?.resource && typeof data.resource === "object") return data.resource;
  return data;
}

export function createResourcePersonMarketplaceActions(set) {
  const mutation = async (type, path, method, body) => {
    assertType(type);
    set({ marketplaceMutationLoading: true, marketplaceMutationError: null });
    try {
      const data = await request(path, { method, body });
      const item = method === "DELETE" ? null : itemOf(data);
      set({
        marketplaceMutationLoading: false,
        marketplaceMutationError: null,
        ...(item ? { marketplaceItem: item } : {}),
      });
      return { success: true, item, data };
    } catch (error) {
      set({ marketplaceMutationLoading: false, marketplaceMutationError: error.message });
      return { success: false, error: error.message };
    }
  };

  return {
    fetchMarketplaceItems: async (type, params = {}) => {
      try {
        assertType(type);
      } catch (error) {
        return { success: false, error: error.message };
      }
      set({
        marketplaceLoading: true,
        marketplaceError: null,
        marketplaceResourceType: type,
      });
      try {
        const data = await request(`/api/resource-person/marketplace/${type}`, { params });
        const items = Array.isArray(data) ? data : data?.items || [];
        const result = {
          items,
          total: data?.total ?? items.length,
          pages: data?.totalPages ?? 1,
          resourceType: data?.resourceType || "",
        };
        set({
          marketplaceItems: result.items,
          marketplaceTotal: result.total,
          marketplacePages: result.pages,
          marketplaceResourceType: result.resourceType || type,
          marketplaceLoading: false,
        });
        return { success: true, ...result };
      } catch (error) {
        set({
          marketplaceItems: [],
          marketplaceTotal: 0,
          marketplacePages: 1,
          marketplaceLoading: false,
          marketplaceError: error.message,
        });
        return { success: false, error: error.message };
      }
    },

    fetchMarketplaceItem: async (type, id) => {
      try {
        assertType(type);
      } catch (error) {
        return { success: false, error: error.message };
      }
      set({ marketplaceItemLoading: true, marketplaceItemError: null });
      try {
        const item = itemOf(await request(`/api/resource-person/marketplace/${type}/${id}`));
        set({ marketplaceItem: item, marketplaceItemLoading: false });
        return { success: true, item };
      } catch (error) {
        set({
          marketplaceItem: null,
          marketplaceItemLoading: false,
          marketplaceItemError: error.message,
        });
        return { success: false, error: error.message };
      }
    },

    createMarketplaceItem: (type, payload) =>
      mutation(type, `/api/resource-person/marketplace/${type}`, "POST", payload),

    updateMarketplaceItem: (type, id, payload) =>
      mutation(type, `/api/resource-person/marketplace/${type}/${id}`, "PATCH", payload),

    deleteMarketplaceItem: (type, id) =>
      mutation(type, `/api/resource-person/marketplace/${type}/${id}`, "DELETE"),

    submitMarketplaceItem: (type, id) =>
      mutation(type, `/api/resource-person/marketplace/${type}/${id}/submit`, "PATCH"),

    unsubmitMarketplaceItem: (type, id) =>
      mutation(type, `/api/resource-person/marketplace/${type}/${id}/unsubmit`, "PATCH"),

    clearMarketplaceItem: () => set({ marketplaceItem: null, marketplaceItemError: null }),
  };
}
